import { useEffect, useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight, Sparkles } from 'lucide-react'
import { ParentPicker, type ParentType } from '../hierarchy/ParentPicker'
import { Button } from '../ui/Button'
import { Textarea } from '../ui/Textarea'
import { SkillService, type Skill, type UpdateSkillRequest } from '../../services/skillService'
import { LIFE_AREA_OPTIONS, type Choice } from '../../utils/taskForm'
import { CADENCE_OPTIONS, LEVEL_OPTIONS } from '../../utils/skillOptions'
import { capabilityOf } from '../../utils/skillWords'

/**
 * "Fill the gaps" for a skill (the rail's first block on every item page).
 * One question at a time, as a carousel — Next/Back move, answering closes
 * the gap, nothing is put away. The gaps are what the persona needs to map a
 * path and what keeps the person going: where they are, where they want to
 * get, by when, what it is for, why it matters, how often they will practise.
 */
interface Props {
  skill: Skill
  onChanged: (skill: Skill) => void
  embedded?: boolean
}

type GapKind = 'where' | 'target' | 'belongs' | 'cadence' | 'by' | 'why'
interface Gap { kind: GapKind; question: string; hint: string }

function gapsOf(s: Skill): Gap[] {
  const out: Gap[] = []
  if (!s.current_level) out.push({ kind: 'where', question: 'Where are you with it right now?', hint: 'helps me map the path' })
  if (!s.target_level) out.push({ kind: 'target', question: 'Where do you want to get?', hint: 'helps me map the path' })
  if (!s.parent?.title && !s.parent_goal_id && !(s.life_areas && s.life_areas.length) && !s.life_area) out.push({ kind: 'belongs', question: 'What is it for?', hint: 'helps me map the path' })
  if (!s.practice_cadence) out.push({ kind: 'cadence', question: 'How often will you practise?', hint: 'helps me plan it' })
  if (!s.target_date) out.push({ kind: 'by', question: 'By when, roughly?', hint: 'helps me keep you going' })
  if (!s.description) out.push({ kind: 'why', question: 'Why does it matter to you?', hint: 'helps me keep you going' })
  return out
}

function Chips({ options, value, onChange, label }: { options: Choice[]; value: string; onChange: (v: string) => void; label: string }) {
  return (
    <div className="flex flex-wrap gap-2" role="group" aria-label={label}>
      {options.map((o) => {
        const on = value === o.value
        return (
          <button key={o.value} type="button" aria-pressed={on} onClick={() => onChange(on ? '' : o.value)}
                  className={`min-h-touch rounded-full border px-3.5 py-2 text-sm font-medium transition-colors ${on ? 'border-brand bg-brand text-on-brand' : 'border-strong bg-surface text-secondary hover:border-brand'}`}>
            {o.label}
          </button>
        )
      })}
    </div>
  )
}

const LEVEL_CHIPS: Choice[] = LEVEL_OPTIONS.map((o) => ({ value: o.value, label: o.label }))

export function SkillGapsCard({ skill, onChanged, embedded = false }: Props) {
  const gaps = useMemo(() => gapsOf(skill), [skill])
  const [idx, setIdx] = useState(0)
  const [answer, setAnswer] = useState<Record<string, string>>({})
  const [parent, setParent] = useState<{ parent_type: ParentType | ''; parent_id: number | null }>({ parent_type: '', parent_id: null })
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  useEffect(() => { if (idx > gaps.length - 1) setIdx(Math.max(0, gaps.length - 1)) }, [gaps.length, idx])
  useEffect(() => { setAnswer({}); setErr(null) }, [idx])
  const gap = gaps[idx] ?? null
  const go = (d: number) => { if (gaps.length) setIdx((i) => (i + d + gaps.length) % gaps.length) }

  const save = async (payload: UpdateSkillRequest) => {
    setBusy(true); setErr(null)
    try {
      const svc = new SkillService()
      await svc.updateSkill(String(skill.id), payload)
      onChanged(await svc.getSkill(String(skill.id)))
      setAnswer({}); setParent({ parent_type: '', parent_id: null })
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not save that')
    } finally {
      setBusy(false)
    }
  }

  const submit = async () => {
    if (!gap) return
    let payload: UpdateSkillRequest | null = null
    switch (gap.kind) {
      case 'where': if (answer.level) payload = { current_level: answer.level }; break
      case 'target': if (answer.level) payload = { target_level: answer.level }; break
      case 'belongs': {
        if (parent.parent_type === 'goal' && parent.parent_id) payload = { parent_goal_id: parent.parent_id }
        else if (answer.life_area) payload = { life_areas: [answer.life_area] }
        break
      }
      case 'cadence': { const v = (answer.own || '').trim() || answer.cadence; if (v) payload = { practice_cadence: v }; break }
      case 'by': if (answer.date) payload = { target_date: answer.date }; break
      case 'why': { const t = (answer.text || '').trim(); if (t) payload = { description: t }; break }
    }
    if (!payload) { setErr('Pick or type an answer first.'); return }
    await save(payload)
  }

  return (
    <div className={embedded ? '' : 'rounded-xl border border-brand-subtle bg-surface p-4 sm:p-5'} data-testid="skill-gaps-card">
      <div className="flex items-center gap-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-primary">{gap ? 'Fill the gaps' : 'Nothing missing'}</div>
          {/* No count once nothing is missing: "6 of 6 said" answered no question (rule 7). */}
          <div className="text-xs text-muted">{gap ? `${gaps.length} detail${gaps.length === 1 ? '' : 's'} missing` : 'Everything the path needs is here.'}</div>
        </div>
        {gaps.length > 1 && (
          <div className="ml-auto flex items-center gap-1 text-app-micro text-muted" data-testid="skill-gaps-nav">
            <button type="button" onClick={() => go(-1)} aria-label="Previous question" className="rounded p-1 hover:bg-surface hover:text-primary"><ChevronLeft className="h-4 w-4" /></button>
            <span className="tabular-nums">{idx + 1} of {gaps.length}</span>
            <button type="button" onClick={() => go(1)} aria-label="Next question" className="rounded p-1 hover:bg-surface hover:text-primary"><ChevronRight className="h-4 w-4" /></button>
          </div>
        )}
      </div>

      {gap && (
        <div className="mt-4 space-y-3">
          <p className="text-sm text-primary" data-testid="skill-gaps-question">{gap.question}</p>

          {(gap.kind === 'where' || gap.kind === 'target') && (
            <div className="space-y-1.5">
              <Chips label={gap.question} options={LEVEL_CHIPS} value={answer.level || ''} onChange={(v) => setAnswer({ ...answer, level: v })} />
              {answer.level && <p className="text-app-micro text-secondary">{capabilityOf(answer.level)}</p>}
            </div>
          )}
          {gap.kind === 'belongs' && (
            <div className="space-y-3">
              <ParentPicker allowedTypes={['goal']} parentType={parent.parent_type} parentId={parent.parent_id}
                            onChange={(f) => setParent({ parent_type: (f.parent_type as ParentType) || '', parent_id: f.parent_id ? Number(f.parent_id) : null })} />
              {!parent.parent_id && (
                <div>
                  <div className="mb-1.5 text-xs text-muted">Or, if it is just for you — which part of life?</div>
                  <Chips label="Life area" options={LIFE_AREA_OPTIONS} value={answer.life_area || ''} onChange={(v) => setAnswer({ ...answer, life_area: v })} />
                </div>
              )}
            </div>
          )}
          {gap.kind === 'cadence' && (
            <div className="space-y-2">
              <Chips label="How often" options={CADENCE_OPTIONS} value={answer.cadence || ''} onChange={(v) => setAnswer({ ...answer, cadence: v, own: '' })} />
              <input type="text" value={answer.own || ''} onChange={(e) => setAnswer({ ...answer, own: e.target.value, cadence: '' })} placeholder="Or in your words — Tuesdays and Thursdays" aria-label="In your words"
                     className="min-h-touch w-full rounded-md border border-strong bg-surface px-2 text-sm text-primary placeholder:text-muted lg:min-h-0 lg:py-1.5" />
            </div>
          )}
          {gap.kind === 'by' && (
            <input type="date" value={answer.date || ''} onChange={(e) => setAnswer({ ...answer, date: e.target.value })} aria-label="Get there by"
                   className="min-h-touch rounded-md border border-strong bg-surface px-2 text-sm text-primary lg:min-h-0 lg:py-1.5" />
          )}
          {gap.kind === 'why' && (
            <Textarea rows={2} value={answer.text ?? ''} onChange={(e) => setAnswer({ ...answer, text: e.target.value })} placeholder="In your words — one line is plenty" aria-label={gap.question} />
          )}

          {err && <p className="text-xs text-bad" role="alert">{err}</p>}
          <div className="flex items-center gap-2">
            <Button size="xs" variant="outline" className="hover:bg-brand-subtle" onClick={() => void submit()} disabled={busy} data-testid="skill-gaps-save">Save</Button>
            {gaps.length > 1 && <button type="button" onClick={() => go(1)} disabled={busy} className="min-h-touch px-3 text-sm text-muted hover:text-secondary" data-testid="skill-gaps-next">Next question</button>}
            <span className="ml-auto inline-flex items-center gap-1 text-app-micro text-muted"><Sparkles className="h-3 w-3" aria-hidden="true" /> {gap.hint}</span>
          </div>
          {gaps.length > 1 && (
            <div className="flex items-center justify-center gap-1.5" aria-hidden="true">
              {gaps.map((g, i) => <button key={g.kind} type="button" tabIndex={-1} onClick={() => setIdx(i)} title={g.question} className={`h-1.5 rounded-full transition-all ${i === idx ? 'w-4 bg-brand' : 'w-1.5 bg-soft hover:bg-strong'}`} />)}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default SkillGapsCard
